/**
 * ZoomControls Component
 *
 * Provides zoom controls for the PDF viewer including:
 * - Zoom in/out buttons
 * - Zoom level dropdown
 */

import { ZoomIn, ZoomOut } from 'lucide-react';

interface ZoomControlsProps {
  zoom: number;
  onZoomChange: (zoom: number) => void;
}

const ZOOM_LEVELS: number[] = [25, 50, 75, 100, 125, 150, 175, 200];

export function ZoomControls({ zoom, onZoomChange }: ZoomControlsProps) {
  const handleZoomIn = () => {
    const currentIndex = ZOOM_LEVELS.findIndex(z => z >= zoom);
    const nextIndex = Math.min(currentIndex + 1, ZOOM_LEVELS.length - 1);
    if (currentIndex === -1) {
      onZoomChange(ZOOM_LEVELS[ZOOM_LEVELS.length - 1]);
    } else {
      onZoomChange(ZOOM_LEVELS[nextIndex]);
    }
  };

  const handleZoomOut = () => {
    const currentIndex = ZOOM_LEVELS.findIndex(z => z >= zoom);
    const prevIndex = Math.max(currentIndex - 1, 0);
    if (currentIndex === -1) {
      onZoomChange(ZOOM_LEVELS[0]);
    } else {
      onZoomChange(ZOOM_LEVELS[prevIndex]);
    }
  };

  const handleZoomSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onZoomChange(parseInt(e.target.value, 10));
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleZoomOut}
        disabled={zoom <= ZOOM_LEVELS[0]}
        className="p-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Zoom out"
      >
        <ZoomOut className="w-4 h-4" />
      </button>

      <select
        value={zoom}
        onChange={handleZoomSelect}
        className="px-3 py-1.5 bg-background border border-border rounded-[var(--radius-button)] text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      >
        {ZOOM_LEVELS.map(level => (
          <option key={level} value={level}>
            {level}%
          </option>
        ))}
      </select>

      <button
        onClick={handleZoomIn}
        disabled={zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
        className="p-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Zoom in"
      >
        <ZoomIn className="w-4 h-4" />
      </button>
    </div>
  );
}
